import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  AnalyticsDashboardResponse,
  AnalyticsProjectStat,
  MaterialEngagementSeriesPoint,
} from '../../services/api';
import {
  AnalyticsChartTooltip,
  CHART_COLORS,
  chartCardSx,
  formatCount,
  formatProjectLabel,
  formatSeriesLabel,
} from './analyticsChartUi';
import { AnalyticsDownloadableChartCard } from './ChartDownloadImageButton';
import MaterialActivityLineChart from './MaterialActivityLineChart';

export type OverviewDashboardPanelCopy = {
  uniqueUsersSubtitle: string;
  newUsersSubtitle: string;
  topProjectEmptyText: string;
};

export type OverviewDashboardPanelProps = {
  data: AnalyticsDashboardResponse | null;
  loading: boolean;
  error: string;
  usersSeries: Array<{ label: string; uniqueUsers: number; newUsers: number; cumulativeNewUsers: number }>;
  activitySeries: Array<{ label: string; activities: number; cumulativeActivities: number }>;
  topProject: AnalyticsProjectStat | null;
  isMonthlyOverview: boolean;
  usersOverTimeOpen: boolean;
  onUsersOverTimeToggle: () => void;
  onBarClick: (data: AnalyticsProjectStat) => void;
  copy: OverviewDashboardPanelCopy;
  chartGridStroke: string;
  chartAxisStroke: string;
  chartTickStyle: { fill: string; fontSize: number };
  /** When true, chart cards show a Download Image button (PE analytics). */
  enableChartDownload?: boolean;
  materialActivity?: {
    loading: boolean;
    error: string;
    series: MaterialEngagementSeriesPoint[];
    metric: 'previews' | 'downloads';
    onMetricChange: (metric: 'previews' | 'downloads') => void;
    onRefresh: () => void;
  };
};

const metricValueSx = {
  fontWeight: 700,
  mt: 0.5,
  color: 'var(--color-text-strong)',
} as const;

const OverviewDashboardPanel: React.FC<OverviewDashboardPanelProps> = ({
  data,
  loading,
  error,
  usersSeries,
  activitySeries,
  topProject,
  isMonthlyOverview,
  usersOverTimeOpen,
  onUsersOverTimeToggle,
  onBarClick,
  copy,
  chartGridStroke,
  chartAxisStroke,
  chartTickStyle,
  enableChartDownload = false,
  materialActivity,
}) => {
  const downloadName = (base: string) => (enableChartDownload ? base : undefined);

  return (
    <>
      {isMonthlyOverview ? (
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
          One bar per month. Cumulative bars carry the running total through each month, so the last
          month matches the range total. Monthly unique users are distinct agents within that month,
          and the first and last month count only the days inside the selected range.
        </Typography>
      ) : null}

      {error ? <Alert severity="error">{error}</Alert> : null}

      {data?.warnings?.map((warning) => (
        <Alert key={`overview-${warning}`} severity="warning">{warning}</Alert>
      ))}

      {loading ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2.5} sx={{ color: 'var(--color-text-strong)' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card sx={chartCardSx}>
                <CardContent sx={{ p: '0 !important' }}>
                  <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                    Unique Users
                  </Typography>
                  <Typography variant="h4" sx={metricValueSx}>
                    {formatCount(data?.uniqueUsers)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)', mt: 1 }}>
                    {copy.uniqueUsersSubtitle}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={chartCardSx}>
                <CardContent sx={{ p: '0 !important' }}>
                  <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                    New Users
                  </Typography>
                  <Typography variant="h4" sx={metricValueSx}>
                    {formatCount(data?.newUsers)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)', mt: 1 }}>
                    {copy.newUsersSubtitle}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  ...chartCardSx,
                  background: 'linear-gradient(135deg, rgba(237,137,54,0.18), rgba(237,137,54,0.04))',
                  borderColor: 'rgba(237,137,54,0.18)',
                }}
              >
                <CardContent sx={{ p: '0 !important' }}>
                  <Typography variant="overline" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                    Top Project
                  </Typography>
                  <Typography variant="h6" sx={{ ...metricValueSx, mt: 0.75 }}>
                    {topProject?.projectName || 'No data'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.78)', mt: 1 }}>
                    {topProject
                      ? `${formatCount(topProject.views)} tracked interactions`
                      : copy.topProjectEmptyText}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sx={{ minWidth: 0 }}>
              <AnalyticsDownloadableChartCard
                title="Users Over Time"
                fileName={downloadName('pe-users-over-time')}
                onTitleClick={onUsersOverTimeToggle}
                titleExpanded={usersOverTimeOpen}
                titleAdornment={
                  usersOverTimeOpen ? (
                    <ExpandLessIcon sx={{ fontSize: 20, color: 'rgba(var(--color-text-rgb), 0.85)' }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 20, color: 'rgba(var(--color-text-rgb), 0.85)' }} />
                  )
                }
                sx={chartCardSx}
              >
                <Collapse in={usersOverTimeOpen} unmountOnExit>
                  <Box sx={{ width: '100%', height: 300, minWidth: 0 }}>
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={usersSeries}>
                        <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
                        <XAxis
                          dataKey="label"
                          stroke={chartAxisStroke}
                          tick={chartTickStyle}
                          tickFormatter={formatSeriesLabel}
                        />
                        <YAxis allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                        <RechartsTooltip
                          content={<AnalyticsChartTooltip />}
                          cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                        />
                        <Bar
                          dataKey="newUsers"
                          name="New Users"
                          fill="#4299E1"
                          radius={[6, 6, 0, 0]}
                          barSize={isMonthlyOverview ? 40 : 22}
                        />
                        <Line
                          type="monotone"
                          dataKey="uniqueUsers"
                          name="Unique Users"
                          stroke="var(--color-accent)"
                          strokeWidth={3}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                </Collapse>
              </AnalyticsDownloadableChartCard>
            </Grid>

            <Grid item xs={12} sx={{ minWidth: 0 }}>
              <AnalyticsDownloadableChartCard
                title="Cumulative New Users"
                fileName={downloadName('pe-cumulative-new-users')}
                sx={chartCardSx}
              >
                <Box sx={{ width: '100%', height: 300, minWidth: 0 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={usersSeries}>
                      <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
                      <XAxis
                        dataKey="label"
                        stroke={chartAxisStroke}
                        tick={chartTickStyle}
                        tickFormatter={formatSeriesLabel}
                      />
                      <YAxis allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                      <RechartsTooltip
                        content={<AnalyticsChartTooltip />}
                        cursor={{ fill: 'rgba(99, 179, 237, 0.14)' }}
                      />
                      <Bar
                        dataKey="cumulativeNewUsers"
                        name="Cumulative New Users"
                        fill="#63B3ED"
                        radius={[6, 6, 0, 0]}
                        barSize={isMonthlyOverview ? 40 : undefined}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </AnalyticsDownloadableChartCard>
            </Grid>

            <Grid item xs={12} sx={{ minWidth: 0 }}>
              <AnalyticsDownloadableChartCard
                title="Cumulative Activities"
                fileName={downloadName('pe-cumulative-activities')}
                sx={chartCardSx}
              >
                <Box sx={{ width: '100%', height: 300, minWidth: 0 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={activitySeries}
                      margin={{ top: 6, right: 8, left: 8, bottom: 6 }}
                    >
                      <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
                      <XAxis
                        dataKey="label"
                        stroke={chartAxisStroke}
                        tick={chartTickStyle}
                        tickFormatter={formatSeriesLabel}
                      />
                      <YAxis allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                      <RechartsTooltip
                        content={<AnalyticsChartTooltip />}
                        cursor={{ fill: 'rgba(237, 137, 54, 0.14)' }}
                      />
                      <Bar
                        dataKey="cumulativeActivities"
                        name="Cumulative Activities"
                        fill="#ED8936"
                        radius={[6, 6, 0, 0]}
                        barSize={isMonthlyOverview ? 40 : undefined}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </AnalyticsDownloadableChartCard>
            </Grid>
          </Grid>

          <AnalyticsDownloadableChartCard
            title="Top Viewed Projects"
            fileName={downloadName('pe-top-viewed-projects')}
            subtitle="By tagged EDM/eBook interactions"
            sx={chartCardSx}
          >
            <Box sx={{ width: '100%', height: 380 }}>
              <ResponsiveContainer>
                <BarChart data={data?.topProjects ?? []} layout="vertical" margin={{ left: 12, right: 12 }}>
                  <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
                  <YAxis
                    type="category"
                    dataKey="projectName"
                    width={160}
                    stroke={chartAxisStroke}
                    tick={chartTickStyle}
                    tickFormatter={formatProjectLabel}
                  />
                  <RechartsTooltip />
                  <Bar
                    dataKey="views"
                    radius={[0, 6, 6, 0]}
                    onClick={(e: { payload?: AnalyticsProjectStat }) => {
                      if (e?.payload) onBarClick(e.payload);
                    }}
                    cursor="pointer"
                  >
                    {(data?.topProjects ?? []).map((entry, index) => (
                      <Cell key={`${entry.projectName}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </AnalyticsDownloadableChartCard>

          {materialActivity ? (
            <MaterialActivityLineChart
              title="Marketing Material Activity"
              loading={materialActivity.loading}
              error={materialActivity.error}
              series={materialActivity.series}
              metric={materialActivity.metric}
              onMetricChange={materialActivity.onMetricChange}
              materialAppSource="project_essential"
              onRefresh={materialActivity.onRefresh}
              chartGridStroke={chartGridStroke}
              chartAxisStroke={chartAxisStroke}
              chartTickStyle={chartTickStyle}
              downloadFileName={downloadName('pe-marketing-material-activity')}
            />
          ) : null}
        </Stack>
      )}
    </>
  );
};

export default OverviewDashboardPanel;
